import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { FileText } from 'lucide-react';
import { useAppContext } from '../../../../context/AppContext';

export default function LibraryMenu({
  isCollapsed,
  libraryExpanded,
  theme,
  isMobile,
}) {
  const navigate = useNavigate();
  const {
    variants = [],
    handleDeleteVariant,
    handleRenameVariant: handleRenameVariantContext,
    handleDuplicateVariant: handleDuplicateVariantContext,
  } = useAppContext();

  const { resumeId } = useParams();
  const [activeMenuId, setActiveMenuId] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);

  const isDark = theme === 'dark';
  const sidebarBorder = isDark ? '#1F1F24' : '#E5E7EB';

  const menuItemStyle = {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '6px 12px',
    fontSize: '11.5px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: isDark ? '#F3F4F6' : '#1F2937',
    fontWeight: 500,
    outline: 'none',
  };

  const handleRenameVariant = (variantId, currentName) => {
    const newName = prompt('Enter new resume name:', currentName);
    if (!newName || newName.trim() === '') return;
    handleRenameVariantContext(variantId, newName);
  };

  const handleDuplicateVariant = async (variantId) => {
    const dup = await handleDuplicateVariantContext(variantId);
    if (dup) {
      navigate(`/app/resume/${dup.id}/chat`);
    }
  };

  const handleDeleteVariantClick = async (variantId) => {
    const isCurrentlyActive = String(resumeId) === String(variantId);
    const success = await handleDeleteVariant(variantId);
    if (success && isCurrentlyActive) {
      navigate('/app/resume/master/chat');
    }
  };

  if (isCollapsed || !libraryExpanded) return null;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        padding: isMobile ? '0 12px 0 16px' : '0 12px 0 28px',
        marginTop: '2px',
        marginBottom: '6px',
      }}
    >
      {variants.map(v => {
        const isSelected = String(resumeId) === String(v.id) || (v.isMaster && resumeId === 'master');
        const showMenuTrigger = hoveredId === v.id || activeMenuId === v.id;

        return (
          <div
            key={v.id}
            style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}
            onMouseEnter={() => setHoveredId(v.id)}
            onMouseLeave={() => {
              setHoveredId(null);
              setActiveMenuId(null);
            }}
          >
            <Link
              to={`/app/resume/${v.id}/chat`}
              style={{
                display: 'flex',
                alignItems: 'center',
                height: '32px',
                padding: '0 10px',
                paddingRight: showMenuTrigger ? '36px' : '10px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: isSelected ? 600 : 500,
                width: '100%',
                textAlign: 'left',
                cursor: 'pointer',
                background: isSelected
                  ? (isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)')
                  : 'transparent',
                color: isSelected
                  ? (isDark ? '#FFFFFF' : '#111827')
                  : (isDark ? '#9CA3AF' : '#4B5563'),
                transition: 'all 0.1s ease',
                outline: 'none',
                textDecoration: 'none',
                boxSizing: 'border-box',
              }}
              onMouseEnter={e => {
                if (!isSelected) {
                  e.currentTarget.style.background = isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.025)';
                }
              }}
              onMouseLeave={e => {
                if (!isSelected) {
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              <FileText size={13} style={{ marginRight: '8px', color: isSelected ? 'var(--brand)' : (isDark ? '#71717a' : '#9ca3af'), flexShrink: 0 }} />
              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                {v.name}
              </div>
            </Link>

            {/* Ellipsis Menu Trigger */}
            {showMenuTrigger && (
              <button
                onClick={e => {
                  e.stopPropagation();
                  setActiveMenuId(activeMenuId === v.id ? null : v.id);
                }}
                style={{
                  position: 'absolute',
                  right: '6px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  border: 'none',
                  background: 'transparent',
                  color: isDark ? '#A1A1AA' : '#6B7280',
                  cursor: 'pointer',
                  fontSize: '11px',
                  fontWeight: 'bold',
                  padding: '2px 4px',
                  borderRadius: '4px',
                  opacity: 0.8,
                  outline: 'none',
                }}
                onMouseEnter={e => (e.currentTarget.style.opacity = 1)}
                onMouseLeave={e => (e.currentTarget.style.opacity = 0.8)}
              >
                •••
              </button>
            )}

            {/* Dropdown Options */}
            {activeMenuId === v.id && (
              <div
                style={{
                  position: 'absolute',
                  top: '28px',
                  right: '6px',
                  background: isDark ? '#1F1F24' : '#FFFFFF',
                  border: `1px solid ${sidebarBorder}`,
                  borderRadius: '6px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  zIndex: 100,
                  width: '100px',
                  padding: '4px 0',
                }}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleRenameVariant(v.id, v.name);
                    setActiveMenuId(null);
                  }}
                  style={menuItemStyle}
                >
                  Rename
                </button>
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    await handleDuplicateVariant(v.id);
                    setActiveMenuId(null);
                  }}
                  style={menuItemStyle}
                >
                  Duplicate
                </button>
                {!v.isMaster && (
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      await handleDeleteVariantClick(v.id);
                      setActiveMenuId(null);
                    }}
                    style={{ ...menuItemStyle, color: '#EF4444' }}
                  >
                    Delete
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
